import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { centers as centersApi } from '@/api/endpoints'
import { Alert, Checkbox, Field, HelpTip, Input, Modal, Spinner } from '@/components/ui'
import { usePlanFeature } from '@/features/billing/PlanFeature'
import BankAccounts from '@/features/centers/BankAccounts'
import { cn } from '@/lib/utils'
import type { Center } from '@/types/api'

type Draft = Partial<Center>

const IDENTITY_FIELDS: Array<[keyof Center, string]> = [
  ['legal_name', 'centers.legalName'],
  ['street', 'centers.street'],
  ['postal_code', 'centers.postalCode'],
  ['city', 'centers.city'],
  ['country_code', 'centers.countryCode'],
  ['ico', 'centers.ico'],
  ['dic', 'centers.dic'],
  ['vat_id', 'centers.vatId'],
  ['registration', 'centers.registration'],
]
const PREFIX_FIELDS: Array<[keyof Center, string]> = [
  ['receipt_prefix', 'centers.receiptPrefix'],
  ['refund_prefix', 'centers.refundPrefix'],
  ['invoice_prefix', 'centers.invoicePrefix'],
  ['invoice_storno_prefix', 'centers.invoiceStornoPrefix'],
]

/**
 * Nastavenia → Strediská (`sql/040`). Doterajšia škola je hlavné stredisko;
 * ďalšie (napr. Donovaly, Šachtičky) majú vlastný cenník a môžu mať vlastnú
 * účtovnú identitu - firmu, účet a rady dokladov. Inštruktori a klienti sú
 * spoloční.
 *
 * Obrazovka je viditeľná vždy (aj hlavné stredisko). Bez balíčka
 * s `multi_center` (od Profi) klik na stredisko či „Pridať“ neotvorí úpravu,
 * ale upozornenie s ponukou prechodu na vyšší balíček.
 */
export default function CentersTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { allowed, from } = usePlanFeature('multi_center')
  const [upsell, setUpsell] = useState(false)
  const { data, isLoading } = useQuery({ queryKey: ['centers'], queryFn: centersApi.list })
  // Vybrané stredisko je v adrese (`?id=`) - po uložení sa obnoví
  // `bootstrap` a obrazovka sa môže znova pripojiť; výber nesmie zmiznúť.
  const [params, setParams] = useSearchParams()
  const raw = params.get('id')
  const selected: number | 'new' | null = raw === 'new' ? 'new' : raw ? Number(raw) : null
  const setSelected = (value: number | 'new' | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === null) next.delete('id')
        else next.set('id', String(value))
        return next
      },
      { replace: true },
    )
  const [draft, setDraft] = useState<Draft>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  const current = typeof selected === 'number' ? data?.find((c) => c.id === selected) : undefined
  useEffect(() => {
    if (allowed && selected === null && data?.length) setSelected(data[0].id)
  }, [allowed, data, selected])
  useEffect(() => {
    setErrors({})
    setDraft(selected === 'new' ? { name: '', active: true, own_identity: false, color: '#3b82f6' } : current ?? {})
  }, [selected, current])

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['centers'] })
    // Výbery stredísk v kalendári a formulároch idú z `bootstrap`.
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
  }
  const onError = (err: unknown) => {
    const data = (err as { response?: { data?: Record<string, unknown> } }).response?.data
    if (data && typeof data === 'object' && !('detail' in data)) {
      setErrors(Object.fromEntries(Object.entries(data).map(([k, v]) => [k, String(Array.isArray(v) ? v[0] : v)])))
      setMessage({ kind: 'error', text: t('centers.fixErrors') })
      return
    }
    const code = apiErrorCode(err)
    setMessage({
      kind: 'error',
      text: code === 'center.inUse' ? t('centers.inUse') : apiErrorMessage(err),
    })
  }
  const save = useMutation({
    mutationFn: () => (selected === 'new' ? centersApi.create(draft) : centersApi.update(selected as number, draft)),
    onSuccess: (center) => {
      setErrors({})
      setMessage({ kind: 'success', text: t('global.saved') })
      setSelected(center.id)
      refresh()
    },
    onError,
  })
  const remove = useMutation({
    mutationFn: (id: number) => centersApi.remove(id),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('centers.deleted') })
      setSelected(null)
      refresh()
    },
    onError,
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  const set = (field: keyof Center) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }))
  const isMain = current?.id === 1

  return (
    <div className="max-w-4xl space-y-3">
      <p className="text-[13px] text-slate-500">{t('centers.intro')}</p>
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <div className="card shrink-0 p-2 md:w-56">
          {data.map((center) => (
            <button
              key={center.id}
              type="button"
              onClick={() => {
                setMessage(null)
                if (allowed) setSelected(center.id)
                else setUpsell(true)
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                selected === center.id ? 'bg-brand-50 font-medium text-brand-700' : 'hover:bg-slate-50',
                !center.active && 'text-slate-400',
              )}
            >
              <span className="h-3 w-3 shrink-0 rounded-full border border-slate-200" style={{ background: center.color || undefined }} />
              <span className="truncate">{center.name}</span>
              {center.own_identity ? <span className="ml-auto text-[10px] uppercase text-slate-400">{t('centers.ownShort')}</span> : null}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setMessage(null)
              if (allowed) setSelected('new')
              else setUpsell(true)
            }}
            className={cn(
              'mt-1 flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm text-action-700 hover:bg-slate-50 disabled:text-slate-400',
              selected === 'new' && 'bg-brand-50',
            )}
          >
            <Plus size={14} />
            {t('centers.add')}
          </button>
        </div>

        {!allowed ? (
          <div className="card min-w-0 flex-1 p-4 text-sm text-slate-600">
            <p>{t('centers.lockedText', { plan: from })}</p>
            <Link to="/settings/subscription#plan" className="btn-primary mt-3 inline-flex">
              {t('centers.upgrade')}
            </Link>
          </div>
        ) : selected !== null ? (
          <form
            className="card min-w-0 flex-1 space-y-3 p-4"
            onSubmit={(e) => {
              e.preventDefault()
              setMessage(null)
              save.mutate()
            }}
          >
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <Field label={t('centers.name')} error={errors.name}>
                <Input value={draft.name ?? ''} maxLength={100} onChange={set('name')} required />
              </Field>
              <Field label={t('centers.color')}>
                <input
                  type="color"
                  className="h-9 w-14 cursor-pointer rounded border border-slate-300"
                  value={draft.color || '#3b82f6'}
                  onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
                />
              </Field>
            </div>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                label={t('centers.active')}
                checked={draft.active ?? true}
                disabled={isMain}
                onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
              />
              <span className="flex items-center gap-1">
                <Checkbox
                  label={t('centers.ownIdentity')}
                  checked={draft.own_identity ?? false}
                  disabled={isMain}
                  onChange={(e) => setDraft((d) => ({ ...d, own_identity: e.target.checked }))}
                />
                <HelpTip label={t('centers.ownIdentity')}>{t('centers.ownIdentityHint')}</HelpTip>
              </span>
            </div>
            {isMain ? <p className="text-xs text-slate-500">{t('centers.mainHint')}</p> : null}

            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t('centers.email')} error={errors.email}>
                <Input value={draft.email ?? ''} onChange={set('email')} />
              </Field>
              <Field label={t('centers.phone')} error={errors.phone}>
                <Input value={draft.phone ?? ''} onChange={set('phone')} />
              </Field>

            </div>

            {/* Účty strediska (`sql/042`) - viac IBAN, jeden predvolený. */}
            <div className="space-y-1">
              <h3 className="pt-1 text-xs font-semibold uppercase text-slate-500">{t('accounts.title')}</h3>
              {typeof selected === 'number' ? (
                <BankAccounts centerId={selected} />
              ) : (
                <p className="text-xs text-slate-500">{t('accounts.afterSave')}</p>
              )}
            </div>

            {draft.own_identity ? (
              <>
                <h3 className="pt-1 text-xs font-semibold uppercase text-slate-500">{t('centers.identity')}</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {IDENTITY_FIELDS.map(([field, label]) => (
                    <Field key={field} label={t(label)} error={errors[field]}>
                      <Input value={String(draft[field] ?? '')} onChange={set(field)} />
                    </Field>
                  ))}
                </div>
                <h3 className="pt-1 text-xs font-semibold uppercase text-slate-500">{t('centers.series')}</h3>
                <p className="text-xs text-slate-500">{t('centers.seriesHint')}</p>
                <div className="grid gap-2 sm:grid-cols-4">
                  {PREFIX_FIELDS.map(([field, label]) => (
                    <Field key={field} label={t(label)} error={errors[field]}>
                      <Input maxLength={10} value={String(draft[field] ?? '')} onChange={set(field)} />
                    </Field>
                  ))}
                </div>
              </>
            ) : null}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button type="submit" className="btn-primary" disabled={save.isPending || (selected === 'new' && !allowed)}>
                {save.isPending ? <Spinner /> : null}
                {t('global.save')}
              </button>
              {current && !isMain ? (
                <button
                  type="button"
                  className="btn-secondary ml-auto text-red-700"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('centers.deleteConfirm', { name: current.name }))) remove.mutate(current.id)
                  }}
                >
                  {t('global.delete')}
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
      </div>

      {upsell ? (
        <Modal
          open
          onClose={() => setUpsell(false)}
          size="sm"
          title={t('centers.lockedTitle')}
          footer={
            <>
              <button type="button" className="btn-secondary" onClick={() => setUpsell(false)}>
                {t('global.close')}
              </button>
              <Link to="/settings/subscription#plan" className="btn-primary" onClick={() => setUpsell(false)}>
                {t('centers.upgrade')}
              </Link>
            </>
          }
        >
          <p className="text-sm text-slate-600">{t('centers.lockedText', { plan: from })}</p>
        </Modal>
      ) : null}
    </div>
  )
}
